import { useCallback, useRef, useState } from "react";
import { useAppContext } from "../../../../AppContext";
import { useFeatureFlag } from "../../../infrastructure/hooks/useFeatureFlag";
import { usePushNotifications } from "../../../infrastructure/hooks/usePushNotifications";
import {
  getNotificationPermission,
  getNotificationsSectionStatus,
  getRestTimerNotificationHint,
  isInAppBrowser,
  isIosSafariTab,
  isPushSupported,
} from "../../../infrastructure/utilities/pushUtility";
import {
  readNotificationPromptHistory,
  recordNotificationPromptDismissal,
  REST_TIMER_NOTIFICATION_HINT_STORAGE_KEY,
} from "../../../infrastructure/utilities/notificationPromptStorageUtility";
import { getErrorToast, getSuccessToast } from "../../../infrastructure/utilities/toastUtility";
import {
  FeatureFlagKey,
  NotificationsSectionStatus,
  PushSubscribeResult,
  RestTimerNotificationHint,
} from "../../../enums";

export interface IRestTimerNotificationHint {
  view: RestTimerNotificationHint;
  isBusy: boolean;
  isIosStepsOpen: boolean;
  onEnable: () => Promise<void>;
  onDismiss: () => void;
  onToggleIosSteps: () => void;
}

/**
 * What the workout rest timer needs from device notifications, behind the
 * enable-app-notifications flag like every device notification:
 * - this device's push endpoint, so the server can ring the phone when it is locked;
 * - the line under the timer that offers to turn notifications on (or, on an iPhone
 *   Safari tab, shows how to add Planny to the Home Screen), with "×" for 30 days.
 * Nothing is checked until refresh() runs as a rest starts; the endpoint is needed the
 * moment the phone locks, when there is no time left to ask the browser for it.
 */
export const useRestTimerNotifications = () => {
  const { currentUser } = useAppContext();
  // Off in a profile a global admin opened from the admin panel: this browser's push
  // subscription is the admin's own, so neither offer it nor hand its endpoint to the timer.
  const flagEnabled =
    useFeatureFlag(FeatureFlagKey.EnableAppNotifications, false) && !currentUser?.impersonatedBy;
  const { subscribe, getActiveEndpoint } = usePushNotifications();
  const endpointRef = useRef<string | null>(null);
  const [status, setStatus] = useState<NotificationsSectionStatus | null>(null);
  const [history, setHistory] = useState(() =>
    readNotificationPromptHistory(REST_TIMER_NOTIFICATION_HINT_STORAGE_KEY)
  );
  const [isBusy, setIsBusy] = useState(false);
  const [isIosStepsOpen, setIsIosStepsOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!flagEnabled) return;
    const endpoint = await getActiveEndpoint();
    endpointRef.current = endpoint;
    setStatus(
      getNotificationsSectionStatus({
        supported: isPushSupported(),
        permission: getNotificationPermission(),
        iosSafariTab: isIosSafariTab(),
        inAppBrowser: isInAppBrowser(),
        hasSubscription: endpoint !== null,
      })
    );
  }, [flagEnabled, getActiveEndpoint]);

  const getPushEndpoint = useCallback(
    () => (flagEnabled ? endpointRef.current : null),
    [flagEnabled]
  );

  const onEnable = useCallback(async () => {
    setIsBusy(true);
    const result = await subscribe();
    setIsBusy(false);

    if (result === PushSubscribeResult.Subscribed) {
      getSuccessToast("Известията са включени. Таймерът ще звъни и при заключен телефон.");
    } else if (result === PushSubscribeResult.Failed) {
      getErrorToast("Известията не успяха да се включат. Опитайте отново.");
    }

    await refresh();
  }, [subscribe, refresh]);

  const onDismiss = useCallback(() => {
    setHistory(recordNotificationPromptDismissal(new Date(), REST_TIMER_NOTIFICATION_HINT_STORAGE_KEY));
    setIsIosStepsOpen(false);
  }, []);

  const onToggleIosSteps = useCallback(() => setIsIosStepsOpen((open) => !open), []);

  const view =
    status === null
      ? null
      : getRestTimerNotificationHint({ flagEnabled, status, history, now: new Date() });

  const hint: IRestTimerNotificationHint | null = view
    ? { view, isBusy, isIosStepsOpen, onEnable, onDismiss, onToggleIosSteps }
    : null;

  return { refresh, getPushEndpoint, hint };
};
